import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus, Search, FileText, ExternalLink, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { RecordStatusBadge } from '../components/record-status-badge'
import { CreateRecordModal } from '../components/create-record-modal'
import { RecordWorkspaceShell } from '../components/record-workspace-shell'
import { useRecordsQuery } from '../api/use-records-query'
import { useRecordsSummaryQuery } from '@/features/summary-card/api/use-summary-card-query'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { cn } from '@/lib/utils/cn'
import type { RecordStatus, RecordsFilter, RecordsSort } from '../api/records-api'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const STATUS_FILTERS: { value: RecordStatus; label: string }[] = [
  { value: 'draft', label: 'پیش‌نویس' },
  { value: 'in_progress', label: 'در جریان' },
  { value: 'submitted_for_review', label: 'ارسال برای بررسی' },
  { value: 'under_review', label: 'در حال بررسی' },
  { value: 'approved', label: 'تأیید شده' },
  { value: 'needs_revision', label: 'نیاز به اصلاح' },
]

// ─── Panel 1: Records List ────────────────────────────────────────────────────

interface RecordsListPanelProps {
  projectId: string
  selectedRecordId: string | null
  onSelectRecord: (id: string) => void
  canEdit: boolean
}

const SORT_OPTIONS: { value: RecordsSort; label: string }[] = [
  { value: 'updated_desc', label: 'آخرین به‌روزرسانی' },
  { value: 'created_desc', label: 'جدیدترین' },
  { value: 'created_asc', label: 'قدیمی‌ترین' },
  { value: 'display_id', label: 'شناسه' },
]

function RecordsListPanel({ projectId, selectedRecordId, onSelectRecord, canEdit }: RecordsListPanelProps) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<RecordStatus[]>([])
  const [showDeleted, setShowDeleted] = useState(false)
  const [sort, setSort] = useState<RecordsSort>('updated_desc')
  const [page, setPage] = useState(0)
  const [showCreate, setShowCreate] = useState(false)
  const debouncedSearch = useDebouncedValue(search, 300)

  const filter: RecordsFilter = {
    search: debouncedSearch || undefined,
    status: statusFilter.length > 0 ? statusFilter : undefined,
    showDeleted,
    sort,
  }

  const { data, isLoading, isFetching } = useRecordsQuery(projectId, filter, page, 25)
  const records = data?.records ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 25)
  const hasActiveFilters = statusFilter.length > 0 || !!debouncedSearch || showDeleted

  const { data: summaryByRecord } = useRecordsSummaryQuery(
    projectId,
    records.map((r) => r.id),
  )

  const toggleStatus = (s: RecordStatus) => {
    setStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    )
    setPage(0)
  }

  const clearFilters = () => {
    setStatusFilter([])
    setSearch('')
    setShowDeleted(false)
    setPage(0)
  }

  // Keyboard nav: ↑/↓ move the selection among the loaded page (ignored while
  // typing in the search box). Enter on a focused row triggers its onClick.
  const handleKeyNav = (e: React.KeyboardEvent) => {
    if (records.length === 0) return
    const tag = (e.target as HTMLElement).tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA') return
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const idx = records.findIndex((r) => r.id === selectedRecordId)
    const next =
      e.key === 'ArrowDown'
        ? Math.min(idx < 0 ? 0 : idx + 1, records.length - 1)
        : Math.max(idx < 0 ? 0 : idx - 1, 0)
    onSelectRecord(records[next].id)
  }

  return (
    <div className="w-full h-full flex flex-col" onKeyDown={handleKeyNav}>
      {/* Header: primary action + search + filters */}
      <div className="p-3 space-y-3 shrink-0 border-b border-border">
        {canEdit && (
          <Button className="w-full gap-1.5" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            سابقه جدید
          </Button>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            placeholder="جستجو در شناسه و فیلدها..."
            className="ps-8 pe-8 h-9 text-sm"
            dir="auto"
          />
          <div className="absolute end-2.5 top-1/2 -translate-y-1/2 flex items-center">
            {isFetching ? (
              <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
            ) : search ? (
              <button
                onClick={() => { setSearch(''); setPage(0) }}
                className="text-muted-foreground hover:text-foreground"
                title="پاک کردن"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Status filters */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-muted-foreground">فیلتر وضعیت</span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-[11px] text-primary hover:underline"
              >
                پاک کردن فیلترها
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {STATUS_FILTERS.map((opt) => {
              const active = statusFilter.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleStatus(opt.value)}
                  aria-pressed={active}
                  className={cn(
                    'text-xs px-2.5 py-1 rounded-full border transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  )}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Sort + deleted toggle + result count */}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value as RecordsSort); setPage(0) }}
            className="h-7 rounded-md border border-border bg-background px-2 text-[11px] text-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label="مرتب‌سازی"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="text-[11px] text-muted-foreground shrink-0">
            {total.toLocaleString('fa-IR')} نتیجه
          </span>
        </div>
        {canEdit && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <Checkbox
              checked={showDeleted}
              onCheckedChange={(c) => { setShowDeleted(c === true); setPage(0) }}
            />
            نمایش حذف‌شده‌ها
          </label>
        )}
      </div>

      {/* Record list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-3 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : records.length === 0 ? (
          hasActiveFilters ? (
            <div className="flex flex-col items-center gap-2 py-12 px-4 text-center">
              <FileText className="w-8 h-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">نتیجه‌ای با این فیلترها یافت نشد</p>
              <button onClick={clearFilters} className="text-xs text-primary hover:underline">
                پاک کردن فیلترها
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-12 px-4 text-center">
              <FileText className="w-8 h-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">هنوز سابقه‌ای ثبت نشده است</p>
              {canEdit && (
                <Button size="sm" className="gap-1.5" onClick={() => setShowCreate(true)}>
                  <Plus className="w-3.5 h-3.5" />
                  ایجاد اولین سابقه
                </Button>
              )}
            </div>
          )
        ) : (
          <div className="divide-y divide-border">
            {records.map((record) => (
              <div
                key={record.id}
                className={cn(
                  'flex items-stretch hover:bg-muted/20 transition-colors group',
                  selectedRecordId === record.id && 'bg-muted/40 border-s-2 border-primary',
                  record.is_deleted && 'opacity-50'
                )}
              >
                <button
                  className="flex-1 text-start px-3 py-3 min-w-0"
                  onClick={() => onSelectRecord(record.id)}
                >
                  <p className="font-mono text-xs font-medium text-foreground truncate">{record.display_id}</p>
                  {(() => {
                    const primary = (summaryByRecord?.get(record.id) ?? []).find((i) => !i.isEmpty)
                    return primary ? (
                      <p className="text-xs text-muted-foreground truncate mt-0.5" title={`${primary.label}: ${primary.value}`}>
                        <span className="text-muted-foreground/70">{primary.label}: </span>
                        <span className="text-foreground/80">{primary.value}</span>
                      </p>
                    ) : null
                  })()}
                  <div className="flex items-center justify-between gap-1 mt-1">
                    <RecordStatusBadge status={record.status} />
                    <span className="text-[10px] text-muted-foreground">{dayjs(record.updated_at).fromNow()}</span>
                  </div>
                </button>
                <a
                  href={`/projects/${projectId}/records/${record.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center px-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                  title="باز کردن در تب جدید"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              قبلی
            </Button>
            <span className="text-[10px] text-muted-foreground">
              {(page + 1).toLocaleString('fa-IR')} / {totalPages.toLocaleString('fa-IR')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              بعدی
            </Button>
          </div>
        )}
      </div>

      {projectId && (
        <CreateRecordModal
          projectId={projectId}
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); onSelectRecord(id) }}
        />
      )}
    </div>
  )
}

// ─── RecordsWorkspacePage ─────────────────────────────────────────────────────

export function RecordsWorkspacePage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { canEdit } = useProjectRole()

  if (!projectId) return null

  return (
    <RecordWorkspaceShell
      projectId={projectId}
      leftPanel={({ selectedRecordId, onSelectRecord }) => (
        <RecordsListPanel
          projectId={projectId}
          selectedRecordId={selectedRecordId}
          onSelectRecord={onSelectRecord}
          canEdit={canEdit}
        />
      )}
    />
  )
}
